/**
 * ColorPicker — a clickable swatch that reveals a ColorWheel popover.
 *
 * Drop-in replacement for <input type="color"> across the app.
 * Accepts and emits hex strings.
 */

import { useCallback, useRef, useState } from "react";
import { ColorWheel } from "./ColorWheel.tsx";
import { Popover } from "./Popover.tsx";
import { COLOR_BORDER_STRONG, RADIUS_LARGE, RADIUS_SM } from "./tokens.ts";

export interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  onChangeStart?: () => void;
  onChangeEnd?: () => void;
  disabled?: boolean;
  /** Swatch size in px.  Default 28. */
  size?: number;
}

const POPOVER_PADDING = 10;
const WHEEL_SIZE = 140;

export function ColorPicker({
  value,
  onChange,
  onChangeStart,
  onChangeEnd,
  disabled,
  size = 28,
}: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const swatchRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => {
    if (!disabled) setOpen((o) => !o);
  }, [disabled]);

  return (
    <div
      ref={swatchRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      {/* Swatch button */}
      <div
        onClick={toggle}
        style={{
          width: size,
          height: size,
          borderRadius: RADIUS_SM,
          border: `1px solid ${COLOR_BORDER_STRONG}`,
          background: value,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.4 : 1,
        }}
      />
      {/* Color wheel popover */}
      <Popover
        anchorRef={swatchRef}
        open={open}
        onClose={() => setOpen(false)}
        style={{
          padding: POPOVER_PADDING,
          background: "rgba(38,38,42,0.95)",
          borderRadius: RADIUS_LARGE,
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        }}
      >
        <ColorWheel
          value={value}
          onChange={onChange}
          onChangeStart={onChangeStart}
          onChangeEnd={onChangeEnd}
          size={WHEEL_SIZE}
        />
      </Popover>
    </div>
  );
}
